import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Badge, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import axios from '../utils/axios';
import './LiveAssistant.css';

/**
 * LiveAssistant Component
 * 
 * A floating overlay that provides real-time transcription and AI assistance
 * during calls/recordings. Uses Web Speech API for instant transcription.
 * 
 * Browser Support:
 * - Chrome: Full support (webkitSpeechRecognition)
 * - Safari: Partial support (SpeechRecognition, may require user gesture)
 * - Firefox: No support (fallback to manual mode)
 * - Edge: Full support (webkitSpeechRecognition)
 */

const LiveAssistant = ({ 
  isRecording = false, 
  onClose,
  onTranscriptUpdate,
  initialPosition = { x: 20, y: 20 },
  audioStream = null // Optional: pass audio stream from screen capture for better quality
}) => {
  // UI State
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState({ width: 380, height: 450 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Transcription Mode: 'realtime' (AssemblyAI), 'whisper-system' (Whisper for system audio), or 'browser' (free/fast)
  const [transcriptionMode, setTranscriptionMode] = useState('realtime');
  
  // Transcription State
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [browserSupport, setBrowserSupport] = useState({ supported: false, browser: '' });
  
  // Key Points State (extracted from transcript)
  const [keyPoints, setKeyPoints] = useState([]);
  
  // AI Suggestions State
  const [suggestions, setSuggestions] = useState([]);
  const [quickTip, setQuickTip] = useState('');
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [lastSuggestionTime, setLastSuggestionTime] = useState(0);
  
  // Query State
  const [queryInput, setQueryInput] = useState('');
  const [queryResponse, setQueryResponse] = useState('');
  const [isQuerying, setIsQuerying] = useState(false);
  const [activeTab, setActiveTab] = useState('transcript'); // 'transcript', 'suggestions', 'query'
  
  // Refs
  const recognitionRef = useRef(null);
  const containerRef = useRef(null);
  const transcriptRef = useRef(null);
  const isListeningRef = useRef(false);
  const shouldRestartRef = useRef(true);
  const suggestionIntervalRef = useRef(null);
  const lastInterimRef = useRef(''); // Track interim text to save it if recognition stops
  
  // Whisper mode refs
  const mediaRecorderRef = useRef(null);
  const whisperIntervalRef = useRef(null);
  const localStreamRef = useRef(null);
  const keepaliveRef = useRef(null); // Watchdog to ensure recognition stays active
  
  // AssemblyAI real-time refs
  const assemblyWsRef = useRef(null);
  const processorRef = useRef(null);
  
  // Check browser support on mount
  useEffect(() => {
    const checkBrowserSupport = () => {
      // Check for Speech Recognition API
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        // Detect browser type
        const userAgent = navigator.userAgent.toLowerCase();
        let browser = 'unknown';
        
        if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
          browser = 'chrome';
        } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
          browser = 'safari';
        } else if (userAgent.includes('edg')) {
          browser = 'edge';
        } else if (userAgent.includes('firefox')) {
          browser = 'firefox';
        }
        
        setBrowserSupport({ supported: true, browser });
        return true;
      }
      
      setBrowserSupport({ supported: false, browser: 'unsupported' });
      return false;
    };
    
    checkBrowserSupport();
  }, []);
  
  // Ref to store extractKeyPoints function for use in recognition
  const extractKeyPointsRef = useRef(null);
  
  // Keep refs in sync with state
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);
  
  // Initialize Speech Recognition
  const initializeSpeechRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser');
      return null;
    }
    
    const recognition = new SpeechRecognition();
    
    // Configuration for optimal real-time transcription
    recognition.continuous = true;           // Keep listening continuously
    recognition.interimResults = true;       // Show results as they come
    recognition.maxAlternatives = 1;         // Only need best result
    recognition.lang = 'en-US';              // Language setting
    
    // Handle results - this is the main transcription handler
    recognition.onresult = (event) => {
      let finalTranscript = '';
      let currentInterim = '';
      
      // Process all results from this event
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        
        if (result.isFinal) {
          finalTranscript += text + ' ';
        } else {
          currentInterim += text;
        }
      }
      
      // Update final transcript
      if (finalTranscript) {
        // Clear the interim ref since this text is now final
        lastInterimRef.current = '';
        setTranscript(prev => prev + finalTranscript);
        setInterimTranscript('');
        // Extract key points from new text using ref
        if (extractKeyPointsRef.current) {
          extractKeyPointsRef.current(finalTranscript);
        }
      } else if (currentInterim) {
        // Track interim text so we can save it if recognition stops unexpectedly
        lastInterimRef.current = currentInterim;
        setInterimTranscript(currentInterim);
      }
    };
    
    // Handle errors
    recognition.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
      
      switch (event.error) {
        case 'not-allowed':
          setError('Microphone access denied. Please allow microphone access.');
          shouldRestartRef.current = false;
          break;
        case 'no-speech':
          // This is normal, just means silence - keep listening
          // Don't change shouldRestartRef - we want to keep going
          break;
        case 'aborted':
          // System aborted (e.g., new recognition started) - this is often normal
          // Keep shouldRestartRef as-is, onend will handle restart if needed
          break;
        case 'audio-capture':
          setError('No microphone found. Please connect a microphone.');
          shouldRestartRef.current = false;
          break;
        case 'network':
          setError('Network error. Check your internet connection.');
          // Still try to restart on network errors
          break;
        default:
          // For other errors, log but keep trying
          console.log(`Speech recognition error: ${event.error}`);
      }
    };
    
    // Handle end - restart if still supposed to be listening
    recognition.onend = () => {
      console.log('Recognition ended, shouldRestart:', shouldRestartRef.current);
      isListeningRef.current = false; // Mark as not listening (will be set true on restart)
      
      // IMPORTANT: Save any interim text that wasn't finalized before recognition stopped
      if (lastInterimRef.current) {
        console.log('Saving interim text:', lastInterimRef.current);
        const savedInterim = lastInterimRef.current + ' ';
        setTranscript(prev => prev + savedInterim);
        lastInterimRef.current = '';
        setInterimTranscript('');
      }
      
      // Only stop if explicitly told to (shouldRestartRef is false)
      if (!shouldRestartRef.current) {
        setIsListening(false);
        return;
      }
      
      // AGGRESSIVE auto-restart for continuous listening
      const attemptRestart = (attempt = 1) => {
        if (!shouldRestartRef.current) return;
        
        try {
          console.log(`Restarting recognition (attempt ${attempt})...`);
          recognitionRef.current.start();
        } catch (e) {
          console.log('Restart error:', e.message);
          if (e.message?.includes('already started')) {
            // Already running, that's fine
            return;
          }
          // Retry with exponential backoff up to 5 attempts
          if (attempt < 5 && shouldRestartRef.current) {
            setTimeout(() => attemptRestart(attempt + 1), 100 * attempt);
          } else if (shouldRestartRef.current) {
            // Last resort: recreate the recognition instance
            console.log('Recreating recognition instance...');
            recognitionRef.current = initializeSpeechRecognition();
            if (recognitionRef.current) {
              try {
                recognitionRef.current.start();
              } catch (e2) {
                console.error('Failed to restart after recreation:', e2);
              }
            }
          }
        }
      };
      
      // Start restart attempts immediately
      setTimeout(() => attemptRestart(1), 10);
    };
    
    recognition.onstart = () => {
      console.log('Recognition started');
      setIsListening(true);
      isListeningRef.current = true;
      setError(null);
    };
    
    return recognition;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dependencies managed via refs
  
  // Extract key points from transcript (simple pattern matching)
  const extractKeyPoints = useCallback((text) => {
    const patterns = [
      // Money patterns
      { regex: /\$[\d,]+(?:\.\d{2})?|\d+(?:,\d{3})*\s*(?:dollars|k|thousand|million)/gi, type: 'money', icon: '💰' },
      // Date patterns
      { regex: /(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}(?:st|nd|rd|th)?(?:\s*,?\s*\d{4})?/gi, type: 'date', icon: '📅' },
      { regex: /(?:next|this)\s+(?:week|month|quarter|year)/gi, type: 'date', icon: '📅' },
      { regex: /Q[1-4]\s*(?:\d{4})?/gi, type: 'date', icon: '📅' },
      // Deadline/timeline
      { regex: /deadline[:\s]+[^.]+/gi, type: 'deadline', icon: '⏰' },
      { regex: /by\s+(?:end\s+of\s+)?(?:the\s+)?(?:week|month|year|january|february|march|april|may|june|july|august|september|october|november|december)/gi, type: 'deadline', icon: '⏰' },
      // Numbers/percentages
      { regex: /\d+(?:\.\d+)?\s*%/g, type: 'percentage', icon: '📊' },
      // Action items
      { regex: /(?:need to|have to|must|should|will)\s+[^.]+/gi, type: 'action', icon: '✅' },
    ];
    
    const newPoints = [];
    
    patterns.forEach(({ regex, type, icon }) => {
      const matches = text.match(regex);
      if (matches) {
        matches.forEach(match => {
          const cleanMatch = match.trim();
          newPoints.push({ text: cleanMatch, type, icon, timestamp: new Date() });
        });
      }
    });
    
    if (newPoints.length > 0) {
      setKeyPoints(prev => {
        // Filter out duplicates
        const existingTexts = prev.map(kp => kp.text.toLowerCase());
        const uniqueNew = newPoints.filter(np => !existingTexts.includes(np.text.toLowerCase()));
        return [...prev, ...uniqueNew].slice(-10); // Keep last 10
      });
    }
  }, []);
  
  // Keep ref updated with latest extractKeyPoints
  useEffect(() => {
    extractKeyPointsRef.current = extractKeyPoints;
  }, [extractKeyPoints]);
  
  // Start listening
  const startListening = useCallback(() => {
    if (!browserSupport.supported) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }
    
    // Enable auto-restart
    shouldRestartRef.current = true;
    
    // Create new recognition instance if needed
    if (!recognitionRef.current) {
      recognitionRef.current = initializeSpeechRecognition();
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        toast.success('Live transcription started');
        
        // Start keepalive watchdog - checks every 5 seconds if recognition is still active
        if (keepaliveRef.current) clearInterval(keepaliveRef.current);
        keepaliveRef.current = setInterval(() => {
          if (shouldRestartRef.current && !isListeningRef.current) {
            console.log('Keepalive: Recognition appears stopped, restarting...');
            try {
              if (recognitionRef.current) {
                recognitionRef.current.start();
              }
            } catch (e) {
              if (!e.message?.includes('already started')) {
                console.log('Keepalive restart failed:', e.message);
              }
            }
          }
        }, 5000);
        
      } catch (e) {
        // Already started - that's fine
        if (e.message?.includes('already started')) {
          console.log('Recognition already running');
        } else {
          console.log('Recognition start error:', e.message);
        }
      }
    }
  }, [browserSupport.supported, initializeSpeechRecognition]);
  
  // Stop listening (browser mode)
  const stopListening = useCallback(() => {
    // Disable auto-restart first
    shouldRestartRef.current = false;
    
    // Stop keepalive
    if (keepaliveRef.current) {
      clearInterval(keepaliveRef.current);
      keepaliveRef.current = null;
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.log('Recognition stop failed:', e);
      }
    }
    
    setIsListening(false);
    isListeningRef.current = false;
    setInterimTranscript('');
  }, []);
  
  // ========== WHISPER MODE (for system audio capture) ==========
  
  // Ref to hold AudioContext for cleanup
  const audioContextRef = useRef(null);
  
  // Start Whisper-based transcription (captures system audio)
  // Uses Web Audio API to extract audio from getDisplayMedia stream
  const startWhisperMode = useCallback(async () => {
    try {
      setInterimTranscript('Requesting screen/tab access...');
      
      // Request screen/tab capture WITH audio
      // User MUST select a Chrome TAB and check "Share audio"
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true, // Video is required by the API
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      
      // Check if we got audio
      const audioTracks = displayStream.getAudioTracks();
      if (audioTracks.length === 0) {
        toast.error('No audio captured! You must: 1) Select a Chrome TAB, 2) Check "Share audio" checkbox');
        displayStream.getTracks().forEach(t => t.stop());
        return;
      }
      
      console.log('Audio track captured:', audioTracks[0].label);
      console.log('Audio settings:', audioTracks[0].getSettings());
      
      // Store the display stream for cleanup
      localStreamRef.current = displayStream;
      
      // === USE WEB AUDIO API TO CREATE AUDIO-ONLY STREAM ===
      // This is the key: MediaRecorder can't record audio-only from getDisplayMedia directly
      // We use AudioContext to create a new audio stream that MediaRecorder can handle
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Create a source from the display stream's audio
      const source = audioContext.createMediaStreamSource(displayStream);
      
      // Create a destination that gives us a new MediaStream
      const destination = audioContext.createMediaStreamDestination();
      
      // Connect source to destination
      source.connect(destination);
      
      // Now we have an audio-only stream that MediaRecorder can use
      const audioOnlyStream = destination.stream;
      console.log('Created audio-only stream with', audioOnlyStream.getAudioTracks().length, 'tracks');
      
      // Set up MediaRecorder with the audio-only stream
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : MediaRecorder.isTypeSupported('audio/webm') 
          ? 'audio/webm' 
          : 'audio/mp4';
      
      console.log('Using mime type:', mimeType);
      
      const mediaRecorder = new MediaRecorder(audioOnlyStream, { 
        mimeType,
        audioBitsPerSecond: 128000
      });
      mediaRecorderRef.current = mediaRecorder;
      
      // Track chunks - the first chunk contains the WebM header which is required
      let headerChunk = null; // Store the first chunk (contains file header)
      let currentChunks = [];
      let chunkCount = 0;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunkCount++;
          console.log('Audio chunk received:', event.data.size, 'bytes, chunk #', chunkCount);
          
          // First chunk contains the WebM header - save it separately
          if (!headerChunk) {
            headerChunk = event.data;
            console.log('Saved header chunk:', headerChunk.size, 'bytes');
          }
          currentChunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        console.log('MediaRecorder stopped');
      };
      
      mediaRecorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
      };
      
      // Handle when user stops sharing
      displayStream.getVideoTracks()[0].onended = () => {
        console.log('User stopped sharing');
        stopWhisperMode();
      };
      
      // Start recording - collect data every 5 seconds
      mediaRecorder.start(5000);
      setIsListening(true);
      setInterimTranscript('Listening to tab audio...');
      toast.success('Capturing tab audio - transcription every 15 seconds');
      
      // Process and send audio every 15 seconds
      whisperIntervalRef.current = setInterval(async () => {
        if (currentChunks.length > 0 && mediaRecorder.state === 'recording') {
          // IMPORTANT: Always include the header chunk for valid WebM files
          // WebM files need the header (EBML + Segment info) to be valid
          const chunksToSend = headerChunk && currentChunks[0] !== headerChunk 
            ? [headerChunk, ...currentChunks] 
            : currentChunks;
          
          const audioBlob = new Blob(chunksToSend, { type: mimeType });
          console.log('Created audio blob:', audioBlob.size, 'bytes from', chunksToSend.length, 'chunks');
          currentChunks = []; // Clear for next batch (but keep headerChunk)
          
          // Send to Whisper if we have enough data (at least 10KB)
          if (audioBlob.size > 10000) {
            setInterimTranscript('Processing audio...');
            await sendToWhisper(audioBlob);
          } else {
            console.log('Audio blob too small, skipping:', audioBlob.size);
            setInterimTranscript('Listening... (waiting for more audio)');
          }
        }
      }, 15000);
      
    } catch (error) {
      console.error('Whisper mode error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Screen sharing was cancelled');
      } else if (error.name === 'NotSupportedError') {
        toast.error('Your browser does not support tab audio capture');
      } else {
        toast.error('Failed to capture audio: ' + error.message);
      }
      setInterimTranscript('');
    }
  }, []);
  
  // Send audio chunk to Whisper API
  // Uses Base64 encoding to avoid multipart upload issues with Railway
  const sendToWhisper = async (audioBlob) => {
    try {
      // Validate blob
      if (!audioBlob || audioBlob.size < 100) {
        console.log('Audio blob too small, skipping:', audioBlob?.size);
        return;
      }
      
      console.log('Sending audio to Whisper:', audioBlob.size, 'bytes, type:', audioBlob.type);
      
      // Convert blob to base64 to avoid multipart upload issues
      const arrayBuffer = await audioBlob.arrayBuffer();
      const base64Audio = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      
      console.log('Converted to base64:', base64Audio.length, 'chars');
      
      // Send as JSON with base64 encoded audio
      const response = await axios.post('/api/live-assist/transcribe', {
        audio: base64Audio,
        mimeType: audioBlob.type,
        size: audioBlob.size
      }, {
        timeout: 30000
      });
      
      if (response.data.success && response.data.text) {
        const newText = response.data.text.trim();
        if (newText) {
          console.log('Transcription received:', newText.substring(0, 50) + '...');
          setTranscript(prev => prev + (prev ? ' ' : '') + newText);
          setInterimTranscript('Listening...');
        }
      }
    } catch (error) {
      console.error('Whisper transcription error:', error.message);
      if (error.response) {
        console.error('Server response:', error.response.status, error.response.data);
      }
      setInterimTranscript('Listening... (transcription pending)');
    }
  };
  
  // Stop Whisper mode
  const stopWhisperMode = useCallback(() => {
    console.log('Stopping Whisper mode...');
    
    // Stop the interval
    if (whisperIntervalRef.current) {
      clearInterval(whisperIntervalRef.current);
      whisperIntervalRef.current = null;
    }
    
    // Stop MediaRecorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.log('MediaRecorder stop error:', e);
      }
    }
    mediaRecorderRef.current = null;
    
    // Close AudioContext
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        console.log('AudioContext close error:', e);
      }
      audioContextRef.current = null;
    }
    
    // Stop all tracks in the display stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => {
        t.stop();
        console.log('Stopped track:', t.kind);
      });
      localStreamRef.current = null;
    }
    
    setIsListening(false);
    setInterimTranscript('');
    toast.success('Stopped capturing audio');
  }, []);
  
  // ========== ASSEMBLYAI REAL-TIME MODE ==========
  // True real-time streaming with ~300ms latency
  // Supports both microphone and system audio sources
  
  const startAssemblyAIMode = useCallback(async (useSystemAudio = false) => {
    try {
      let audioStream;
      
      if (useSystemAudio) {
        // System audio mode - capture from screen/tab
        setInterimTranscript('Select a tab and check "Share audio"...');
        
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true, // Required by API
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });
        
        // Check if we got audio
        const audioTracks = displayStream.getAudioTracks();
        if (audioTracks.length === 0) {
          toast.error('No audio! Select a Chrome TAB and check "Share audio"');
          displayStream.getTracks().forEach(t => t.stop());
          return;
        }
        
        console.log('System audio captured:', audioTracks[0].label);
        audioStream = displayStream;
        
      } else {
        // Microphone mode
        setInterimTranscript('Connecting to real-time transcription...');
        
        audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 16000
          }
        });
        
        console.log('Microphone captured for AssemblyAI');
      }
      
      localStreamRef.current = audioStream;
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to use real-time transcription');
        audioStream.getTracks().forEach(t => t.stop());
        return;
      }
      
      // Connect to our WebSocket server
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.REACT_APP_API_URL 
        ? new URL(process.env.REACT_APP_API_URL).host 
        : 'localhost:5001';
      const wsUrl = `${wsProtocol}//${wsHost}/ws/transcribe?token=${token}&sample_rate=16000`;
      
      console.log('Connecting to WebSocket:', wsUrl.replace(token, 'TOKEN'));
      
      const ws = new WebSocket(wsUrl);
      assemblyWsRef.current = ws;
      
      ws.onopen = async () => {
        console.log('WebSocket connected');
        setInterimTranscript('Connected! Speak now...');
        
        try {
          // Set up audio processing with 16kHz sample rate
          const audioContext = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 16000,
            latencyHint: 'balanced'
          });
          audioContextRef.current = audioContext;
          
          console.log('Audio context sample rate:', audioContext.sampleRate);
          
          const source = audioContext.createMediaStreamSource(audioStream);
          
          // Buffer to accumulate audio for ~100ms chunks
          let audioBufferQueue = new Int16Array(0);
          const targetChunkSamples = Math.floor(16000 * 0.1); // 100ms = 1600 samples
          
          // Use ScriptProcessorNode (AudioWorklet requires separate file serving)
          const processor = audioContext.createScriptProcessor(4096, 1, 1);
          processorRef.current = processor;
          
          processor.onaudioprocess = (e) => {
            if (ws.readyState === WebSocket.OPEN) {
              const inputData = e.inputBuffer.getChannelData(0);
              
              // Convert Float32 to Int16 PCM
              const int16Data = new Int16Array(inputData.length);
              for (let i = 0; i < inputData.length; i++) {
                const s = Math.max(-1, Math.min(1, inputData[i]));
                int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
              }
              
              // Merge into buffer queue
              const merged = new Int16Array(audioBufferQueue.length + int16Data.length);
              merged.set(audioBufferQueue, 0);
              merged.set(int16Data, audioBufferQueue.length);
              audioBufferQueue = merged;
              
              // Send 100ms chunks (1600 samples = 3200 bytes)
              while (audioBufferQueue.length >= targetChunkSamples) {
                const chunk = audioBufferQueue.subarray(0, targetChunkSamples);
                audioBufferQueue = audioBufferQueue.subarray(targetChunkSamples);
                
                // Send as raw binary Uint8Array
                const uint8Chunk = new Uint8Array(chunk.buffer, chunk.byteOffset, chunk.byteLength);
                ws.send(uint8Chunk);
              }
            }
          };
          
          source.connect(processor);
          processor.connect(audioContext.destination);
          
          setIsListening(true);
          toast.success('Real-time transcription started');
        } catch (audioError) {
          console.error('Audio setup error:', audioError);
          toast.error('Failed to set up audio: ' + audioError.message);
        }
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'session_start') {
            // v3 session started
            console.log('AssemblyAI session started:', message.session_id);
            setInterimTranscript('Listening... speak now');
          } else if (message.type === 'partial') {
            // Show partial/interim results
            if (message.text) {
              setInterimTranscript(message.text);
            }
          } else if (message.type === 'final') {
            // Add final results to transcript
            if (message.text && message.text.trim()) {
              setTranscript(prev => prev + (prev ? ' ' : '') + message.text.trim());
              setInterimTranscript('');
            }
          } else if (message.type === 'terminated') {
            // Session ended with stats
            console.log('Session ended:', message.audio_duration, 'seconds of audio');
            if (message.credits_used) {
              toast.info(`Session ended: ${message.credits_used} credit${message.credits_used > 1 ? 's' : ''} used`);
            }
          } else if (message.type === 'disconnected') {
            // AssemblyAI disconnected (timeout or error)
            console.log('AssemblyAI disconnected:', message.message);
            // Don't show error toast - the onclose handler will handle it
          } else if (message.type === 'error') {
            console.error('Transcription error:', message.error);
            toast.error('Transcription error: ' + message.error);
          } else if (message.error) {
            console.error('WebSocket error:', message.error);
            toast.error(message.error);
            stopAssemblyAIMode();
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        toast.error('Connection error');
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket closed, code:', event.code, 'reason:', event.reason);
        // Only stop listening if we didn't intentionally close it
        // Code 1000 = normal closure, 1005 = no status (often timeout)
        if (assemblyWsRef.current === ws) {
          // Connection was closed unexpectedly - could be timeout
          if (event.code !== 1000) {
            console.log('Unexpected WebSocket close - may have timed out');
            toast.warning('Connection closed - click Start to reconnect');
          }
          setIsListening(false);
          setInterimTranscript('');
        }
      };
      
    } catch (error) {
      console.error('AssemblyAI mode error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied');
      } else {
        toast.error('Failed to start real-time transcription: ' + error.message);
      }
      setInterimTranscript('');
    }
  }, []);
  
  const stopAssemblyAIMode = useCallback(() => {
    console.log('Stopping AssemblyAI mode...');
    
    // Close WebSocket
    if (assemblyWsRef.current) {
      if (assemblyWsRef.current.readyState === WebSocket.OPEN) {
        assemblyWsRef.current.send(JSON.stringify({ terminate_session: true }));
      }
      assemblyWsRef.current.close();
      assemblyWsRef.current = null;
    }
    
    // Stop audio processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    
    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    // Stop microphone
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    
    setIsListening(false);
    setInterimTranscript('');
    toast.success('Stopped real-time transcription');
  }, []);
  
  // Unified start/stop based on mode
  const handleStartListening = useCallback(() => {
    if (transcriptionMode === 'realtime') {
      startAssemblyAIMode(false); // Mic mode
    } else if (transcriptionMode === 'whisper-system') {
      startAssemblyAIMode(true); // System audio mode - now uses AssemblyAI real-time!
    } else {
      startListening();
    }
  }, [transcriptionMode, startAssemblyAIMode, startListening]);
  
  const handleStopListening = useCallback(() => {
    if (transcriptionMode === 'realtime' || transcriptionMode === 'whisper-system') {
      stopAssemblyAIMode(); // Both use AssemblyAI now
    } else {
      stopListening();
    }
  }, [transcriptionMode, stopAssemblyAIMode, stopListening]);
  
  // Auto-start when recording starts (optional - user can also manually start)
  useEffect(() => {
    if (isRecording && browserSupport.supported && !isListening) {
      startListening();
    }
    // Note: We don't auto-stop when recording stops - let user control that
  }, [isRecording, browserSupport.supported, isListening, startListening]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, []);
  
  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript, interimTranscript]);
  
  // Notify parent when transcript changes
  useEffect(() => {
    if (onTranscriptUpdate && transcript) {
      onTranscriptUpdate(transcript);
    }
  }, [transcript, onTranscriptUpdate]);
  
  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.live-assistant-header')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };
  
  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - 350, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    }
    if (isResizing) {
      const newWidth = Math.max(320, Math.min(600, e.clientX - position.x));
      const newHeight = Math.max(300, Math.min(700, e.clientY - position.y));
      setSize({ width: newWidth, height: newHeight });
    }
  }, [isDragging, isResizing, dragOffset, position.x, position.y]);
  
  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };
  
  // Handle resize start
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };
  
  // Add/remove mouse event listeners for dragging and resizing
  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, handleMouseMove]);
  
  // Clear transcript
  const clearTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setKeyPoints([]);
    setSuggestions([]);
    setQuickTip('');
    setQueryResponse('');
  }, []);
  
  // Fetch AI suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!transcript || transcript.length < 50 || isLoadingSuggestions || !aiEnabled) {
      return;
    }
    
    // Don't fetch too frequently (minimum 15 seconds between requests)
    const now = Date.now();
    if (now - lastSuggestionTime < 15000) {
      return;
    }
    
    setIsLoadingSuggestions(true);
    setLastSuggestionTime(now);
    
    try {
      const response = await axios.post('/api/live-assist/suggest', {
        transcript: transcript
      });
      
      if (response.data.success) {
        setSuggestions(response.data.suggestions || []);
        setQuickTip(response.data.quickTip || '');
        
        // Update key points from AI if available
        if (response.data.keyPoints && response.data.keyPoints.length > 0) {
          setKeyPoints(prev => {
            const newPoints = response.data.keyPoints
              .filter(kp => !prev.some(p => p.text.toLowerCase() === kp.toLowerCase()))
              .map(kp => ({ text: kp, type: 'ai', icon: '🤖' }));
            return [...prev, ...newPoints].slice(-10);
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      if (error.response?.status === 402) {
        toast.error('Insufficient credits for AI suggestions');
        setAiEnabled(false);
      }
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [transcript, isLoadingSuggestions, aiEnabled, lastSuggestionTime]);
  
  // Auto-fetch suggestions every 20 seconds when recording
  useEffect(() => {
    if (isRecording && isListening && aiEnabled && transcript.length > 50) {
      suggestionIntervalRef.current = setInterval(() => {
        fetchSuggestions();
      }, 20000); // Every 20 seconds
      
      // Also fetch immediately if we have enough transcript
      if (suggestions.length === 0) {
        fetchSuggestions();
      }
    }
    
    return () => {
      if (suggestionIntervalRef.current) {
        clearInterval(suggestionIntervalRef.current);
      }
    };
  }, [isRecording, isListening, aiEnabled, transcript.length, fetchSuggestions, suggestions.length]);
  
  // Handle query submission
  const handleQuerySubmit = async (e) => {
    e.preventDefault();
    
    if (!queryInput.trim() || !transcript) {
      toast.error('Please enter a question and ensure there is transcript content');
      return;
    }
    
    setIsQuerying(true);
    setQueryResponse('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/live-assist/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          transcript: transcript,
          question: queryInput
        })
      });
      
      if (!response.ok) {
        if (response.status === 402) {
          toast.error('Insufficient credits for query');
          setIsQuerying(false);
          return;
        }
        throw new Error('Query failed');
      }
      
      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                setQueryResponse(prev => prev + data.content);
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
      
      setQueryInput('');
    } catch (error) {
      console.error('Query error:', error);
      toast.error('Failed to process query');
    } finally {
      setIsQuerying(false);
    }
  };
  
  // Render minimized view
  if (isMinimized) {
    return (
      <div 
        className="live-assistant-minimized"
        style={{ left: position.x, top: position.y }}
        onClick={() => setIsMinimized(false)}
      >
        <span className="live-assistant-minimized-icon">🤖</span>
        <span className="live-assistant-minimized-text">Live Assistant</span>
        {isListening && <span className="live-indicator pulse"></span>}
      </div>
    );
  }
  
  return (
    <div
      ref={containerRef}
      className="live-assistant-container"
      style={{ 
        left: position.x, 
        top: position.y,
        width: size.width,
        height: size.height
      }}
      onMouseDown={handleMouseDown}
    >
      <Card className="live-assistant-card" style={{ height: '100%' }}>
        {/* Header */}
        <div className="live-assistant-header">
          <div className="live-assistant-title">
            <span className="live-assistant-icon">🤖</span>
            <span>Live Assistant</span>
            {isListening && (
              <Badge bg="success" className="ms-2 live-badge">
                <span className="live-dot"></span> LIVE
              </Badge>
            )}
          </div>
          <div className="live-assistant-controls">
            <Button 
              variant="link" 
              size="sm" 
              className="control-btn"
              onClick={() => setIsMinimized(true)}
              title="Minimize"
            >
              ─
            </Button>
            <Button 
              variant="link" 
              size="sm" 
              className="control-btn close-btn"
              onClick={onClose}
              title="Close"
            >
              ✕
            </Button>
          </div>
        </div>
        
        <Card.Body className="live-assistant-body">
          {/* Browser Support Warning */}
          {!browserSupport.supported && (
            <div className="browser-warning">
              <span>⚠️</span>
              <span>Speech recognition not supported. Use Chrome, Edge, or Safari.</span>
            </div>
          )}
          
          {/* Error Display */}
          {error && (
            <div className="error-message">
              <span>❌</span>
              <span>{error}</span>
            </div>
          )}
          
          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button 
              className={`tab-btn ${activeTab === 'transcript' ? 'active' : ''}`}
              onClick={() => setActiveTab('transcript')}
            >
              📝 Transcript
            </button>
            <button 
              className={`tab-btn ${activeTab === 'suggestions' ? 'active' : ''}`}
              onClick={() => setActiveTab('suggestions')}
            >
              💡 AI Tips
              {isLoadingSuggestions && <Spinner size="sm" className="ms-1" />}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'query' ? 'active' : ''}`}
              onClick={() => setActiveTab('query')}
            >
              💬 Ask
            </button>
          </div>
          
          {/* Tab Content */}
          <div className="tab-content">
            {/* Transcript Tab */}
            {activeTab === 'transcript' && (
              <>
                <div className="section-header">
                  <span>Live Transcript</span>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={clearTranscript}
                    disabled={!transcript}
                  >
                    Clear
                  </Button>
                </div>
                
                <div className="transcript-container" ref={transcriptRef}>
                  {transcript || interimTranscript ? (
                    <>
                      <span className="final-transcript">{transcript}</span>
                      <span className="interim-transcript">{interimTranscript}</span>
                    </>
                  ) : (
                    <span className="transcript-placeholder">
                      {isListening 
                        ? 'Listening... Start speaking to see transcription.' 
                        : 'Click "Start Listening" to begin transcription.'}
                    </span>
                  )}
                </div>
                
                {/* Key Points Section */}
                {keyPoints.length > 0 && (
                  <>
                    <div className="section-header mt-2">
                      <span>📊 Key Points</span>
                    </div>
                    <div className="key-points-container">
                      {keyPoints.map((point, index) => (
                        <div key={index} className="key-point">
                          <span className="key-point-icon">{point.icon}</span>
                          <span className="key-point-text">{point.text}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
            
            {/* Suggestions Tab */}
            {activeTab === 'suggestions' && (
              <>
                <div className="section-header">
                  <span>AI Suggestions</span>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check 
                      type="switch"
                      id="ai-toggle"
                      checked={aiEnabled}
                      onChange={(e) => setAiEnabled(e.target.checked)}
                      label=""
                      title={aiEnabled ? 'AI enabled' : 'AI disabled'}
                    />
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={fetchSuggestions}
                      disabled={isLoadingSuggestions || !transcript || transcript.length < 50}
                    >
                      {isLoadingSuggestions ? <Spinner size="sm" /> : '🔄'}
                    </Button>
                  </div>
                </div>
                
                {/* Quick Tip */}
                {quickTip && (
                  <div className="quick-tip">
                    <span className="quick-tip-icon">⚡</span>
                    <span>{quickTip}</span>
                  </div>
                )}
                
                {/* Suggestions List */}
                <div className="suggestions-container">
                  {suggestions.length > 0 ? (
                    suggestions.map((suggestion, index) => (
                      <div key={index} className="suggestion-item">
                        <span className="suggestion-icon">💡</span>
                        <span>{suggestion}</span>
                      </div>
                    ))
                  ) : (
                    <div className="suggestions-placeholder">
                      {isLoadingSuggestions ? (
                        <div className="text-center">
                          <Spinner size="sm" className="me-2" />
                          Analyzing conversation...
                        </div>
                      ) : transcript.length < 50 ? (
                        'Need more transcript content for suggestions...'
                      ) : (
                        'Click refresh to get AI suggestions based on the conversation.'
                      )}
                    </div>
                  )}
                </div>
                
                {/* Key Points from AI */}
                {keyPoints.length > 0 && (
                  <>
                    <div className="section-header mt-2">
                      <span>📊 Detected Points</span>
                    </div>
                    <div className="key-points-container">
                      {keyPoints.slice(0, 5).map((point, index) => (
                        <div key={index} className="key-point">
                          <span className="key-point-icon">{point.icon}</span>
                          <span className="key-point-text">{point.text}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
            
            {/* Query Tab */}
            {activeTab === 'query' && (
              <>
                <div className="section-header">
                  <span>Ask About This Call</span>
                </div>
                
                <Form onSubmit={handleQuerySubmit} className="query-form">
                  <Form.Group className="mb-2">
                    <Form.Control
                      type="text"
                      placeholder="What concerns have they mentioned?"
                      value={queryInput}
                      onChange={(e) => setQueryInput(e.target.value)}
                      disabled={isQuerying || !transcript}
                      className="query-input"
                    />
                  </Form.Group>
                  <Button 
                    type="submit"
                    variant="primary" 
                    size="sm"
                    disabled={isQuerying || !queryInput.trim() || !transcript}
                    className="w-100"
                  >
                    {isQuerying ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Thinking...
                      </>
                    ) : (
                      '🔍 Ask'
                    )}
                  </Button>
                </Form>
                
                {/* Query Response */}
                {queryResponse && (
                  <div className="query-response">
                    <div className="query-response-header">
                      <span>🤖 Answer</span>
                    </div>
                    <div className="query-response-content">
                      {queryResponse}
                    </div>
                  </div>
                )}
                
                {/* Sample Questions */}
                {!queryResponse && (
                  <div className="sample-questions">
                    <p className="sample-questions-title">Try asking:</p>
                    <button 
                      className="sample-question"
                      onClick={() => setQueryInput('What are the main topics discussed?')}
                    >
                      What are the main topics discussed?
                    </button>
                    <button 
                      className="sample-question"
                      onClick={() => setQueryInput('What action items were mentioned?')}
                    >
                      What action items were mentioned?
                    </button>
                    <button 
                      className="sample-question"
                      onClick={() => setQueryInput('What concerns or objections came up?')}
                    >
                      What concerns or objections came up?
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Mode Toggle */}
          <div className="mode-toggle mb-2">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${transcriptionMode === 'realtime' ? 'active' : ''}`}
                onClick={() => !isListening && setTranscriptionMode('realtime')}
                disabled={isListening}
                title="Real-time AI transcription via microphone (~300ms latency)"
              >
                🎤 Real-time
              </button>
              <button 
                className={`mode-btn ${transcriptionMode === 'whisper-system' ? 'active' : ''}`}
                onClick={() => !isListening && setTranscriptionMode('whisper-system')}
                disabled={isListening}
                title="Real-time system audio - captures Zoom/Teams/YouTube (~300ms latency)"
              >
                🖥️ System
              </button>
              <button 
                className={`mode-btn ${transcriptionMode === 'browser' ? 'active' : ''}`}
                onClick={() => !isListening && setTranscriptionMode('browser')}
                disabled={isListening}
                title="Browser speech recognition - free but less accurate"
              >
                ⚡ Free
              </button>
            </div>
            <div className="mode-hint">
              {transcriptionMode === 'realtime' 
                ? '🎤 Mic only: Your voice in real-time' 
                : transcriptionMode === 'whisper-system'
                  ? '🖥️ System Audio: Zoom/Teams/YouTube in real-time!'
                  : '⚡ Browser mode: Free, instant but less accurate'}
            </div>
          </div>
          
          {/* Controls */}
          <div className="assistant-controls">
            {!isListening ? (
              <Button 
                variant="success" 
                size="sm"
                onClick={handleStartListening}
                disabled={transcriptionMode === 'browser' && !browserSupport.supported}
                className="w-100"
              >
                {transcriptionMode === 'realtime' 
                  ? '🎤 Start Real-time' 
                  : transcriptionMode === 'whisper-system'
                    ? '🖥️ Capture System Audio'
                    : '⚡ Start (Browser)'}
              </Button>
            ) : (
              <Button 
                variant="danger" 
                size="sm"
                onClick={handleStopListening}
                className="w-100"
              >
                ⏹️ Stop Listening
              </Button>
            )}
          </div>
          
          {/* Browser Info */}
          <div className="browser-info">
            {transcriptionMode === 'realtime' ? (
              <span className="text-muted">
                ✓ Real-time AI (1 credit per 5 min)
              </span>
            ) : transcriptionMode === 'whisper-system' ? (
              <span className="text-muted">
                ✓ System audio (1 credit per 5 min)
              </span>
            ) : browserSupport.supported ? (
              <span className="text-muted">
                ✓ {browserSupport.browser.charAt(0).toUpperCase() + browserSupport.browser.slice(1)} supported (free)
              </span>
            ) : (
              <span className="text-warning">
                ⚠ Use Chrome, Edge, or Safari for live transcription
              </span>
            )}
          </div>
        </Card.Body>
        
        {/* Resize Handle */}
        <div 
          className="resize-handle"
          onMouseDown={handleResizeStart}
          title="Drag to resize"
        >
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path d="M10 0L0 10M10 4L4 10M10 8L8 10" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        </div>
      </Card>
    </div>
  );
};

export default LiveAssistant;
